import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Fab,
  useTheme,
  useMediaQuery,
  keyframes,
  Button,
  Avatar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SellIcon from '@mui/icons-material/Sell';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';

import { styled, alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

import { useToastContext } from './toast-context';
import { useDialogContext } from './dialog-context';
import { useTrigger } from './useTrigger';
import { services } from '../pages/Sell/services';
import { downloadExportFile } from '../pages/Sell/Sell';
import { UploadDialog } from './upload/uploadDialog';
import logo from '../assets/narin-logo2.webp';

// --- CONSTANTS ---
const DRAWER_WIDTH = 260; // Slightly wider for better visual balance

// --- ANIMATIONS ---
const slideIn = keyframes`
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

// --- STYLED COMPONENTS ---

const MainLayoutRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#f1f5f9', // Slate 100 - Professional Dashboard Background
  overflow: 'hidden',
  direction: 'ltr',
}));

// Modern Drawer Styling
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #e2e8f0', // Subtle border
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.02)', // Very soft shadow
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      position: 'relative',
      height: '100vh',
    },
  },
}));

// Navigation Item Styling
const NavItem = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'active',
})(({ theme, active }) => ({
  margin: '8px 16px',
  borderRadius: '12px',
  padding: '10px 16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',

  // Active State
  ...(active && {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', // Blue Gradient
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
    },
    '& .MuiListItemIcon-root': {
      color: '#ffffff',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      fontWeight: 700,
    },
  }),

  // Inactive State
  ...(!active && {
    color: '#64748b', // Slate 500
    '&:hover': {
      backgroundColor: '#f8fafc', // Slate 50
      color: '#1e293b', // Slate 800
      transform: 'translateX(-4px)', // Move slightly left on hover (RTL)
      '& .MuiListItemIcon-root': {
        color: '#3b82f6',
      },
    },
    '& .MuiListItemIcon-root': {
      color: '#94a3b8', // Slate 400
      transition: 'color 0.3s ease',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 500,
    },
  }),
}));

const ActionButton = styled(Button)(({ theme, gradient }) => ({
  borderRadius: '14px',
  padding: '10px 20px',
  fontWeight: 700,
  fontSize: '0.85rem',
  textTransform: 'none',
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: gradient,
  color: '#fff',
  width: '100%',
  justifyContent: 'flex-start',
  gap: '12px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    filter: 'brightness(110%)',
  },
}));

// Modern Toast Notification
const ModernSnackbarContent = styled('div')(({ theme, severity }) => ({
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  padding: '12px 20px',
  borderRadius: '16px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
  border: '1px solid #e2e8f0',
  borderRight: `6px solid ${
    severity === 'success'
      ? '#10b981'
      : severity === 'error'
        ? '#ef4444'
        : '#3b82f6'
  }`, // Colored indicator bar
  minWidth: '300px',
  maxWidth: '450px',
  animation: `${slideIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
}));

const SlideTransition = props => <Slide {...props} direction="up" />;

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Context Hooks
  const { trigger } = useTrigger();
  const { openDialog } = useDialogContext();
  const { open, message, variant, closeToast } = useToastContext();

  const [mobileOpen, setMobileOpen] = useState(false);
  // const [rows, setRows] = useState([]);

  // useEffect(() => {
  //   services.getInvoiceList().then(data => {
  //     if (Array.isArray(data)) {
  //       data.sort(compare);
  //       setRows(data);
  //     }
  //   });
  // }, [trigger]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = path => location.pathname.startsWith(path);

  const navItems = [
    {
      text: 'عملیات فروش',
      icon: <SellIcon fontSize="small" />,
      path: '/sell',
    },
    {
      text: 'عملیات بازخرید',
      icon: <ShoppingBasketIcon fontSize="small" />,
      path: '/buyback',
    },
    {
      text: 'نمودار‌ها',
      icon: <ShowChartIcon fontSize="small" />,
      path: '/chart',
    },
    {
      text: 'گزارش‌ها',
      icon: <ReceiptLongIcon fontSize="small" />,
      path: '/transactions',
    },
  ];

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* --- Logo Area --- */}
      <Box
        sx={{
          py: 4,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderBottom: '1px solid #f1f5f9',
          mb: 1,
        }}
      >
        <Avatar
          src={logo}
          variant="square"
          sx={{
            width: 'auto',
            height: 70,
            mb: 1,
            '& img': { objectFit: 'contain' },
          }}
        />
      </Box>

      {/* --- Navigation --- */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {navItems.map(item => {
          const active = isActive(item.path);
          return (
            <NavItem
              key={item.text}
              active={active}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  letterSpacing: '0.3px',
                }}
              />
            </NavItem>
          );
        })}
      </List>

      {/* --- Action Buttons & Footer --- */}
      <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <ActionButton
            gradient="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" // Teal Gradient
            onClick={() => downloadExportFile(rows)}
            startIcon={<FileDownloadRoundedIcon />}
          >
            دانلود خروجی روزانه
          </ActionButton>

          <ActionButton
            gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" // Indigo Gradient
            onClick={() => openDialog('آپلود فایل', <UploadDialog />)}
            startIcon={<CloudUploadRoundedIcon />}
          >
            ثبت لیست تحویل
          </ActionButton>
        </Stack>

        <NavItem
          onClick={() => {
            localStorage.clear();
            navigate('/');
          }}
          sx={{
            mx: 0,
            borderRadius: '10px',
            color: '#ef4444',
            '&:hover': {
              bgcolor: '#fef2f2',
              color: '#dc2626',
              '& .MuiListItemIcon-root': { color: '#dc2626' },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: '#ef4444' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="خروج از حساب"
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
          />
        </NavItem>
      </Box>
    </Box>
  );

  return (
    <MainLayoutRoot>
      {/* Mobile Toggle Button */}
      <Fab
        color="primary"
        aria-label="open drawer"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          display: { md: 'none' },
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          boxShadow: '0 12px 24px rgba(37, 99, 235, 0.4)',
        }}
      >
        <MenuIcon />
      </Fab>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile View */}
        <StyledDrawer
          variant="temporary"
          anchor="right" // Keep anchor left as per your RTL setup logic
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </StyledDrawer>

        {/* Desktop View */}
        <StyledDrawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' } }}
          open
        >
          {drawerContent}
        </StyledDrawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          p: { xs: 2, md: 4 },
          position: 'relative',
          overflowY: 'auto',
          height: '100vh',
          transition: 'all 0.3s ease',
        }}
      >
        {children}
      </Box>

      {/* Custom Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={SlideTransition}
        sx={{ zIndex: 2000, bottom: { xs: 90, md: 24 } }}
      >
        <ModernSnackbarContent severity={variant}>
          <Box sx={{ mr: 1.5, display: 'flex' }}>
            {/* Status Dot */}
            {variant === 'success' && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                  boxShadow: '0 0 8px #10b981',
                }}
              />
            )}
            {variant === 'error' && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#ef4444',
                  boxShadow: '0 0 8px #ef4444',
                }}
              />
            )}
            {variant === 'info' && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#3b82f6',
                  boxShadow: '0 0 8px #3b82f6',
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ flex: 1, color: '#1e293b', ml: 1 }}
          >
            {message}
          </Typography>
          <IconButton
            size="small"
            onClick={closeToast}
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </ModernSnackbarContent>
      </Snackbar>
    </MainLayoutRoot>
  );
}

function compare(a, b) {
  if (new Date(a.createdAt) < new Date(b.createdAt)) return 1;
  if (new Date(a.createdAt) > new Date(b.createdAt)) return -1;
  return 0;
}
